import { ApolloClient } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { DistributionBatchObjectTypeSortEnum } from "#graphql/supergraphEnums";
import { GetDistributionBatchesForCanopyPaymentBatchCreationDocument } from "#graphql/supergraphHooks";
import {
  type DistributionBatchesFilters,
  type GetDistributionBatchesForCanopyPaymentBatchCreationQuery,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { DEFAULT_EARLIEST_EFFECTIVE_DATE } from "../GridFilters/DistributionDateFilter/constants";
import { DistributionDateFilterModel } from "../GridFilters/DistributionDateFilter/types";
import { SupportedColumns } from "./columnDefinitions";
import { toLoadSuccessParams } from "./utils";

const DEFAULT_SORT = [
  DistributionBatchObjectTypeSortEnum.EffectiveDateDesc,
  DistributionBatchObjectTypeSortEnum.IdDesc,
];

/**
 * Attempts to calculate a cursor for a given offset.
 * This assumes the backend uses base64-encoded offsets in the format "arrayconnection:{offset}".
 *
 * @param offset - The zero-based offset to encode as a cursor
 * @returns A base64-encoded cursor string
 */
export const calculateCursorFromOffset = (offset: number): string => {
  // Common Relay cursor format: base64("arrayconnection:{offset}")
  return btoa(`arrayconnection:${offset}`);
};

/**
 * Builds the sort parameter for the GraphQL query based on the AG Grid sort model.
 * Supports sorting by effective date (distribution date).
 * Default sort is by effective date descending (newest first).
 */
export const buildDistributionSort = (
  sortModel?: AgGrid.SortModelItem[] | null,
): DistributionBatchObjectTypeSortEnum[] => {
  if (!sortModel || sortModel.length === 0) {
    return DEFAULT_SORT;
  }

  const sort = sortModel[0];
  if (sort.colId === (SupportedColumns.DistributionDate as string)) {
    // Always include IdDesc as secondary sort for consistent ordering
    return sort.sort === "desc"
      ? [
          DistributionBatchObjectTypeSortEnum.EffectiveDateDesc,
          DistributionBatchObjectTypeSortEnum.IdDesc,
        ]
      : [
          DistributionBatchObjectTypeSortEnum.EffectiveDateAsc,
          DistributionBatchObjectTypeSortEnum.IdDesc,
        ];
  }

  return DEFAULT_SORT;
};

export const buildDistributionInputFilters = (
  filterModel?: AgGrid.FilterModel | null,
): DistributionBatchesFilters => {
  const filters: DistributionBatchesFilters = {
    automatedPaymentsEnabled: true,
  };

  const dateFilterModel = filterModel?.[
    SupportedColumns.DistributionDate
  ] as DistributionDateFilterModel;

  if (!dateFilterModel) {
    filters.earliestEffectiveDate = DEFAULT_EARLIEST_EFFECTIVE_DATE;
  } else if (dateFilterModel.date) {
    filters.earliestEffectiveDate = dateFilterModel.date;
  }

  if (filterModel?.[SupportedColumns.ClientName]) {
    filters.arenaIds = [filterModel[SupportedColumns.ClientName]];
  }

  if (filterModel?.[SupportedColumns.EntityName]) {
    filters.entityIds = [filterModel[SupportedColumns.EntityName]];
  }

  return filters;
};

/**
 * This class is used by AG Grid to fetch distribution batches (paginated) via GQL
 * and pass them into the table.
 *
 * The distribution batches are passed into toLoadSuccessParams which transforms
 * the data into row objects that are passed into AGGrid. toLoadSuccessParams also informs
 * AG Grid of the total number of rows so it knows when to stop paginating.
 *
 * This data source converts AG Grid's offset-based pagination to cursor-based pagination
 * used by the GraphQL API. It supports forward/backward navigation and jumping to arbitrary pages.
 */
export class DistributionBatchesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<object>;
  private rowCountPerPage: number;
  private refInputFilters: DistributionBatchesFilters;
  private refSort: DistributionBatchObjectTypeSortEnum[];

  // Cursor tracking for pagination - maps row position to cursor
  private cursors: Map<number, string> = new Map();

  constructor(client: ApolloClient<object>, rowCountPerPage: number) {
    this.client = client;
    this.rowCountPerPage = rowCountPerPage;
    this.refInputFilters = buildDistributionInputFilters();
    this.refSort = DEFAULT_SORT;
  }

  /**
   * Handles error cases by calling the AG Grid fail callback
   */
  private handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  /**
   * Resets the cursor cache (called when filters or sorts change)
   */
  public resetCursors(): void {
    this.cursors.clear();
  }

  /**
   * Determines the cursor to use for pagination based on the requested start row.
   * First checks the cache, then calculates from offset if not found.
   */
  private getCursorForRow(startRow: number): string | undefined {
    if (startRow === 0) {
      return undefined;
    }

    // Try to get the cursor from our cache
    const cachedCursor = this.cursors.get(startRow);
    if (cachedCursor) {
      return cachedCursor;
    }

    // If we don't have the cursor (e.g., user jumped to a page),
    // try to calculate it from the offset
    // This assumes cursors are base64-encoded offsets (Relay format)
    return calculateCursorFromOffset(startRow - 1);
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const { api, request } = params;
    const { filterModel, sortModel, startRow = 0, endRow = this.rowCountPerPage } = request;

    api.showLoadingOverlay();

    const inputFilters = buildDistributionInputFilters(filterModel);
    const sort = buildDistributionSort(sortModel);

    // Reset cursors when filters or sort changes
    if (!isEqual(this.refInputFilters, inputFilters) || !isEqual(this.refSort, sort)) {
      this.resetCursors();
      this.refInputFilters = inputFilters;
      this.refSort = sort;
    }

    const after = this.getCursorForRow(startRow);

    this.client
      .query<GetDistributionBatchesForCanopyPaymentBatchCreationQuery>({
        query: GetDistributionBatchesForCanopyPaymentBatchCreationDocument,
        context: clientContext(),
        fetchPolicy: "no-cache",
        variables: {
          filters: inputFilters,
          first: this.rowCountPerPage,
          after,
          sort,
        },
      })
      .then((response) => {
        const pageInfo = response.data.distributionBatches?.pageInfo;

        // Store the end cursor for the next page
        if (pageInfo?.endCursor) {
          this.cursors.set(endRow, pageInfo.endCursor);
        }

        const successParams = toLoadSuccessParams(response.data);

        if (successParams.resp.rowCount === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }

        params.success(successParams.resp);
      })
      .catch((e: Error) => {
        addError(formatError(e));
        toast.error(`Fetching distribution batches failed: ${e.message}`);
        api.hideOverlay();
        this.handleError(params);
      });
  }
}
